import React, { useState } from 'react';
import styled from 'styled-components';
import SaveButton from '@ui-elements/SaveButton';
import BasicModal from '@ui-parts/BasicModal';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { Box, FormControl, Typography, RadioGroup } from '@mui/material';
import Radio from '@ui-elements/Radio';
import FormControlLabel from '@mui/material/FormControlLabel';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import { PALETTE, FONT_SIZES, FONT_WEIGHTS } from '~/assets/styles/base';
import { CREDIT_CHECK } from '~/constants';
import { callThunk, updateCreditCheck, fetchSavedDeliverySettings } from '~/api';
import { GMOASK_STATUS } from '~/constants/api';

const CustomModalHeader = styled(Box)`
  padding: 30px 51px 17px 51px;
`;

const StyledTypography = styled(Typography)`
  display: flex;
  align-items: center;
  margin-top: 10px;
`;

const StyledIcon = styled(WarningRoundedIcon)`
  font-size: 2.4rem;
  margin-right: 6px;
`;

const Note = styled.div`
  display: inline-flex;
  align-items: center;
  font-size: ${FONT_SIZES.body3};
  font-weight: ${FONT_WEIGHTS.base};
  color: ${PALETTE.error.main};
  margin-left: 16px;
`;

const ModalFooterInner = styled.div`
  padding-bottom: 28px;
  div[color='error'] {
    margin: -5px 0 15px;
  }
`;

const StyledBox = styled(Box)`
  padding: 0 16px;
`;

const StyledRadioGroup = styled(RadioGroup)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const RadioBox = styled.div`
  width: 300px;
  height: 75px;
  margin-top: 10px;
  padding: 15px 20px;
  border: solid ${PALETTE.border.frame} 1px;
  border-radius: 15px;
  background: ${PALETTE.white};
  text-align: center;
  .MuiRadio-root {
    margin: 0 7px 0 0;
  }
`;

function CreditCheckModal({ open, handleClose }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const survey = useSelector((state) => state.survey);
  const [creditCheck, setCreditCheck] = useState(true);

  const onCloseModal = () => {
    setCreditCheck(true);
    handleClose();
  };

  const onClickSave = async () => {
    const params = {
      surveyId: survey.id,
      result: creditCheck,
    };

    const { payload } = await dispatch(
      callThunk({
        action: updateCreditCheck,
        payload: params,
      })
    );

    // 審査フロー枠の表示更新のため、fetchSavedDeliverySettingsを呼ぶ
    /* istanbul ignore next OK以外はエラー処理に移る為、帰ってこないが念の為 */
    if (payload.status === GMOASK_STATUS.OK) {
      await dispatch(
        callThunk({
          action: fetchSavedDeliverySettings,
          payload: { surveyId: survey.id },
        })
      );

      onCloseModal();
    }
  };

  return (
    <BasicModal
      open={open}
      width="764px"
      dataTestId="CreditCheckModal"
      titleProps={{
        customModalTitle: [
          <CustomModalHeader key="targetSamplesDomProps">
            <StyledTypography
              variant="title1"
              fontWeight="bold"
              component="div"
              data-testid="uiParts-CustomModalTitle"
            >
              <div>{t('adminSurvey.JudgementBox.creditCheck.modal.head')}</div>
              <Note variant="title1">
                <StyledIcon />
                {t('adminSurvey.JudgementBox.creditCheck.modal.headNote')}
              </Note>
            </StyledTypography>
          </CustomModalHeader>,
        ],
      }}
      handleClose={() => onCloseModal()}
      buttons={[
        <ModalFooterInner key="ModalFooterInner">
          <SaveButton
            onClick={onClickSave}
            data-testid="CreditCheckModal-SaveButton"
            sx={{ width: 148 }}
          />
        </ModalFooterInner>,
      ]}
    >
      <StyledBox>
        <FormControl component="fieldset" error fullWidth>
          <StyledRadioGroup
            name="creditCheck"
            onChange={(e) => {
              setCreditCheck(e.target.value === CREDIT_CHECK.APPROVED.toString());
            }}
            value={creditCheck}
          >
            <RadioBox>
              <FormControlLabel
                control={
                  <Radio
                    color="primary"
                    data-testid="creditCheck-radio-approved"
                    value={CREDIT_CHECK.APPROVED.toString()} // stringにしないとwarningになる
                  />
                }
                label={
                  <Typography>
                    {t('adminSurvey.JudgementBox.creditCheck.modal.approved')}
                  </Typography>
                }
              />
            </RadioBox>
            <RadioBox>
              <FormControlLabel
                control={
                  <Radio
                    color="primary"
                    data-testid="creditCheck-radio-rejected"
                    value={CREDIT_CHECK.REJECTED.toString()}
                  />
                }
                label={
                  <Typography>
                    {t('adminSurvey.JudgementBox.creditCheck.modal.rejected')}
                  </Typography>
                }
              />
            </RadioBox>
          </StyledRadioGroup>
        </FormControl>
      </StyledBox>
    </BasicModal>
  );
}

CreditCheckModal.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
};

export default CreditCheckModal;
